import { createContext, useContext, useState, useCallback } from 'react';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Global settings
  const [settings, setSettings] = useState({
    currency: 'GBP',
    locale: 'en-GB',
    vatRate: 0.20,
    soundEnabled: true,
    darkMode: false,
  });

  // Bar cart (separate from table orders)
  const [barCart, setBarCart] = useState([]);
  const [barCustomerName, setBarCustomerName] = useState('');

  // Real-time connection status
  const [wsConnected, setWsConnected] = useState(false);

  // Currency formatter
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat(settings.locale, { 
      style: 'currency', 
      currency: settings.currency 
    }).format(value);
  }, [settings.locale, settings.currency]);

  // Calculate VAT (extract from VAT-inclusive price)
  const calculateVat = useCallback((amount) => {
    return amount - (amount / (1 + settings.vatRate));
  }, [settings.vatRate]);

  // Bar cart operations
  const addToBarCart = useCallback((item) => {
    setBarCart(prev => {
      const existingIndex = prev.findIndex(
        c => c.menu_item_id === item.menu_item_id && 
             (c.modifiers?.length === 0 || !c.modifiers) &&
             !c.size
      );

      if (existingIndex >= 0) {
        const newCart = [...prev];
        newCart[existingIndex].quantity += 1;
        newCart[existingIndex].subtotal = newCart[existingIndex].price * newCart[existingIndex].quantity;
        return newCart;
      }
      
      return [...prev, { ...item, quantity: 1, subtotal: item.price }];
    });
  }, []);

  const updateBarCartQuantity = useCallback((index, delta) => {
    setBarCart(prev => {
      const newCart = [...prev];
      newCart[index].quantity += delta;
      if (newCart[index].quantity <= 0) {
        newCart.splice(index, 1);
      } else {
        newCart[index].subtotal = newCart[index].price * newCart[index].quantity;
      }
      return newCart;
    });
  }, []);

  const updateBarCartItem = useCallback((index, updates) => {
    setBarCart(prev => {
      const newCart = [...prev];
      newCart[index] = { ...newCart[index], ...updates };
      return newCart;
    });
  }, []);

  const removeFromBarCart = useCallback((index) => {
    setBarCart(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearBarCart = useCallback(() => {
    setBarCart([]);
    setBarCustomerName('');
  }, []);

  const getBarCartTotal = useCallback(() => {
    return barCart.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  }, [barCart]);

  // Update settings
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  const value = {
    // Settings
    settings,
    updateSettings,
    
    // Utilities
    formatCurrency,
    calculateVat,
    
    // Bar cart
    barCart,
    barCustomerName,
    setBarCustomerName,
    addToBarCart,
    updateBarCartQuantity,
    updateBarCartItem,
    removeFromBarCart,
    clearBarCart,
    getBarCartTotal,
    
    // WebSocket
    wsConnected,
    setWsConnected,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
